<template>
  <div class="p-3">
    <p>{{ $t("checkout_by_phone.instruction") }}</p>

    <form @submit.prevent="handlePhoneSearch" @input="markChanges">
      <div class="form-group">
        <label class="checkout-info">
          {{ $t("checkout_by_phone.label_checkout_date") }}
        </label>
        <input
          class="form-control text-center"
          type="text"
          v-model="checkout_date"
          readonly
          :disabled="isCheckoutNotAvailable || is_searching"
        />
      </div>

      <div class="form-group">
        <label class="checkout-info">
          {{ $t("checkout_by_phone.label_phone_number") }}
          <small class="text-danger">*</small>
        </label>
        
        <input
          type="text"
          class="mt-2 form-control"
          v-model="phone_raw"
          :placeholder="$t('checkout_by_phone.phone_placeholder')"
          required
          :disabled="isCheckoutNotAvailable || is_searching"
          @keypress="onlyNumbersAndPlus"
        />
      </div>

      <div class="text-right">
        <button
          type="submit"
          class="btn btn-form-control btn-custom-primary"
          :disabled="!phone_raw || isCheckoutNotAvailable || is_searching"
        >
          {{ $t("visit-information-form.confirm") }}
        </button>
      </div>

      <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">
        {{ error_msg }}
      </div>

      <div
        class="alert alert-danger mt-3 mb-0"
        role="alert"
        v-for="(i, k) in invalid_code_error"
        :key="k"
      >
        {{ i }}
      </div>
    </form>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapActions } from "vuex";
import { onlyNumbersAndPlus  } from "../../../utils/helpers";

export default {
  name: "CheckoutFormByPhone",
  props: {
    facility: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isCheckoutNotAvailable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      phone_raw: "",
      phone_country_code: "",
      phone_number: "",
      checkout_date: moment().format("YYYY/MM/DD"),
      invalid_code_error: [],
      error_msg: "",
      is_searching: false,
    };
  },
  methods: {
    ...mapActions("checkin-site", ["setBooking"]),
    onlyNumbersAndPlus,
    phoneCountryChange(v) {
      this.phone_country_code = v.dialCode;
    },

    phoneInput(v, o) {
      this.phone_number = o.number.significant;
    },

    markChanges() {
      this.$emit("mark-changes");
    },

    resetForm() {
      this.phone_raw = '';
      this.phone_country_code = '';
      this.phone_number = '';
      this.error_msg = '';
      this.invalid_code_error = [];
    },

    async handlePhoneSearch() {
      this.invalid_code_error = [];
      this.error_msg = "";
      this.is_searching = true;

      const searchPhone = this.phone_raw;
      const todayFormatted = moment().format('YYYYMMDD');

      const phoneSearchPayload = {
        facility_id: this.facility.id,
        mode: "phone-checkout",
        search: searchPhone,
        checkout_date: todayFormatted,
        onDemand: true,
      };

      try {
        const phoneResponse = await this.$lionheart.post(
          "/facility-booking/list",
          phoneSearchPayload
        );

        if (
          !phoneResponse.data ||
          !phoneResponse.data.data ||
          phoneResponse.data.data.length === 0
        ) {
          this.invalid_code_error.push(
            this.$t("checkout_by_phone.error_no_reservation")
          );
          this.is_searching = false;
          setTimeout(() => {
            this.invalid_code_error = [];
          }, 5000);
          return;
        }

        if (phoneResponse.data.data.length > 1) {
          this.invalid_code_error.push(
            this.$t("checkout_by_phone.error_multiple_reservation")
          );
          this.is_searching = false;
          setTimeout(() => {
            this.invalid_code_error = [];
          }, 5000);
          return;
        }

        const foundBooking = phoneResponse.data.data[0];

        let bookingCheckoutDate = foundBooking.parsed?.checkout_date;
        bookingCheckoutDate = this.normalizeDate(bookingCheckoutDate);
        
        if (!bookingCheckoutDate || bookingCheckoutDate !== todayFormatted) {
          this.invalid_code_error.push(this.$t('checkout_by_phone.error_no_reservation'));
          this.is_searching = false;
          setTimeout(() => { this.invalid_code_error = []; }, 5000);
          return;
        }

        const email =
          foundBooking.facility_visitor_log?.visitor?.user?.email ||
          foundBooking.metadata?.email_address ||
          foundBooking.parsed?.email_address ||
          "";
        const pass = foundBooking.pass;

        if (!pass) {
          this.invalid_code_error.push("Could not retrieve booking details");
          this.is_searching = false;
          setTimeout(() => {
            this.invalid_code_error = [];
          }, 5000);
          return;
        }

        this.is_searching = false;

        this.$emit("booking-found", phoneResponse.data);

      } catch (error) {
        console.log("Error:", error);
        this.is_searching = false;
        setTimeout(() => {
          this.invalid_code_error = [];
        }, 5000);
      }
    },

    normalizeDate(dateStr) {
      // Try parsing with known formats, fallback to moment’s auto-parse
      const formats = [
        'YYYY-MM-DD',
        'YYYYMMDD',
        'MM/DD/YYYY',
        'DD/MM/YYYY',
        'YYYY/MM/DD',
        'MMM DD, YYYY',
      ];

      const parsed = moment(dateStr, formats, true);
      
      // If not valid, try without strict parsing
      if (!parsed.isValid()) {
        return null; // or throw new Error("Invalid date format")
      }

      return parsed.format('YYYYMMDD');
    }
  },
};
</script>

<style lang="scss" scoped>
input[type="text"].text-center {
  text-align: center;
}

.btn-form-control {
  min-width: 156px;
  border-radius: 20px;
}

.btn-custom-primary {
  color: white;
  border: 1px solid #1aa8de !important;
  background-color: #1aa8de !important;

  &:not([disabled]):hover {
    border: 1px solid #0b89bb !important;
    background-color: #0b89bb !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #1aa8de !important;
    border: 1px solid #1aa8de !important;
  }
}
</style>
